import { useMemo } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { useTranslation } from 'react-i18next'

import { Alert, Box as DarkBox, Modal } from '@hivewatch/dark'

import { useUpdateDeviceMutation } from '@/graphql/generated/hooks'
import { useAppSelector } from '@/redux/hooks'
import { selectMe } from '@/redux/me/meSlice'
import { trackEvent } from '@/utils/analytics'

import { FLOOR_PLAN_CHECKPOINTS_ANALYTICS } from '../constants/constants'
import { TourPlanI } from '../types/types'

export interface DarkRemoveCheckPointModalIProps {
  isOpen: boolean
  onClose: () => void
  checkpoint: any
  floorId: string
  facilityId: string
  tourPlans: TourPlanI[]
}

const REFETCH_QUERIES = [
  'FloorDevicesCount',
  'FacilityDevicesByFloors',
  'FloorsFacilityCheckpoints',
]

export const DarkRemoveCheckPointModal = ({
  isOpen,
  onClose,
  checkpoint,
  floorId,
  facilityId,
  tourPlans,
}: DarkRemoveCheckPointModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const me = useAppSelector(selectMe)
  const { shouldEnableDeleteCheckpoint } = usePermissions()
  const { showSuccess, showError } = useToasts()
  const [updateDevice, { loading: isLoading }] = useUpdateDeviceMutation()

  const tourPlansWithCheckpoint = useMemo(
    () =>
      tourPlans?.filter((plan) => plan.checkpointIds.includes(checkpoint?.id)),
    [tourPlans, checkpoint]
  )
  const isBlockedByTourPlans = tourPlansWithCheckpoint?.length > 0

  const analyticsProps = {
    checkpoint_id: checkpoint?.id,
    facility_id: facilityId,
    floor_id: floorId,
    role: me?.roleName,
  }

  const handleRemoveCheckPoint = async () => {
    try {
      await updateDevice({
        variables: { input: { id: checkpoint?.id, x: null, y: null } },
        refetchQueries: REFETCH_QUERIES,
      })
      showSuccess(t('floors:plan_remove_checkpoint_success'))
      onClose()
      trackEvent(
        FLOOR_PLAN_CHECKPOINTS_ANALYTICS.mapCheckpointRemove,
        analyticsProps
      )
    } catch (e) {
      showError(t('floors:plan_remove_checkpoint_error'))
    }
  }

  const handleOnClose = () => {
    onClose()
    trackEvent(
      FLOOR_PLAN_CHECKPOINTS_ANALYTICS.mapCheckpointRemoveCancel,
      analyticsProps
    )
  }

  return (
    <Modal
      colorScheme={colorScheme}
      isOpen={isOpen}
      isSaveDestructive
      isSaveDisabled={!shouldEnableDeleteCheckpoint || isBlockedByTourPlans}
      isSaveLoading={isLoading}
      onClose={handleOnClose}
      onSave={handleRemoveCheckPoint}
      saveButtonLabel={t('common:remove')}
      showCancelButton
      size='compact'
      title={t('floors:plan_remove_checkpoint_title')}
    >
      <DarkBox
        data-testid='DarkRemoveCheckPointModal:body'
        display='flex'
        flexDirection='column'
        gap='16px'
      >
        <DarkBox
          color={
            colorScheme === 'light' ? 'light.text.secondary' : 'text.secondary'
          }
          textStyle='caption'
        >
          {t('floors:plan_remove_checkpoint_description')}
        </DarkBox>
        {isBlockedByTourPlans && (
          <Alert
            colorScheme={colorScheme}
            description={tourPlansWithCheckpoint
              .map(({ name }) => `• ${name}`)
              .join('\n')}
            size='compact'
            title={t('floors:plan_remove_checkpoint_tourplan_warning')}
            variant='warning'
          />
        )}
      </DarkBox>
    </Modal>
  )
}
